import React from 'react';
import { scoreValidity } from '../../utils/gapScorer';

const CRITERIA = [
  {
    key: 'unresolved',
    label: 'Unresolved',
    question: 'Is this gap genuinely unresolved in the current literature?',
    hint: '1 = largely resolved | 5 = completely unresolved',
  },
  {
    key: 'contributory',
    label: 'Contributory',
    question: 'Will addressing this gap make a meaningful contribution to knowledge?',
    hint: '1 = minimal contribution | 5 = significant contribution',
  },
  {
    key: 'specific',
    label: 'Specific',
    question: 'Is the gap clearly and specifically defined?',
    hint: '1 = vague/broad | 5 = precisely defined',
  },
  {
    key: 'attainable',
    label: 'Attainable',
    question: 'Can this gap be addressed within your resources, time and scope?',
    hint: '1 = not feasible | 5 = very feasible',
  },
];

export default function Step5_ValidityScorer({ scores, onChange, onNext, onBack }) {
  const update = (key, val) => onChange({ ...scores, [key]: val });

  const result = scoreValidity(scores);
  const allScored = CRITERIA.every((c) => scores[c.key] && parseInt(scores[c.key]) > 0);

  const verdictColor =
    result.percentage >= 80 ? '#22c55e' :
    result.percentage >= 60 ? '#f59e0b' :
    result.percentage >= 40 ? '#ef4444' : '#6b7280';

  return (
    <div className="gf-step">
      <div className="gf-step-header">
        <span className="gf-step-badge">Phase 5</span>
        <h2>Validity &amp; Feasibility Scorer</h2>
        <p>
          Rate your identified gap on four criteria (1–5 scale).
          This ensures the gap is academically credible and practically achievable.
        </p>
      </div>

      <div className="gf-validity-grid">
        {CRITERIA.map((crit) => (
          <div key={crit.key} className="gf-validity-card">
            <div className="gf-validity-header">
              <span className="gf-validity-label">{crit.label}</span>
              <span className="gf-validity-score-display">
                {scores[crit.key] ? `${scores[crit.key]}/5` : '—'}
              </span>
            </div>
            <p className="gf-validity-question">{crit.question}</p>
            <p className="gf-hint">{crit.hint}</p>
            <div className="gf-score-buttons">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  className={`gf-score-btn ${parseInt(scores[crit.key]) === n ? 'active' : ''}`}
                  onClick={() => update(crit.key, n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── Live Score Display ── */}
      {allScored && (
        <div className="gf-validity-result" style={{ borderColor: verdictColor }}>
          <div className="gf-validity-donut">
            <svg viewBox="0 0 120 120" width="120" height="120">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#e5e7eb" strokeWidth="12" />
              <circle
                cx="60" cy="60" r="50"
                fill="none"
                stroke={verdictColor}
                strokeWidth="12"
                strokeDasharray={`${(result.percentage / 100) * 314} 314`}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
              />
              <text x="60" y="55" textAnchor="middle" fontSize="22" fontWeight="700" fill={verdictColor}>
                {result.total}
              </text>
              <text x="60" y="72" textAnchor="middle" fontSize="12" fill="#6b7280">
                /20
              </text>
            </svg>
          </div>
          <div className="gf-validity-verdict">
            <h3 style={{ color: verdictColor }}>{result.verdict}</h3>
            <p>Validity Score: <strong>{result.percentage}%</strong></p>
            <div className="gf-validity-breakdown">
              {result.breakdown.map((b) => (
                <div key={b.criterion} className="gf-validity-row">
                  <span>{b.criterion}</span>
                  <div className="gf-mini-progress">
                    <div style={{ width: `${(b.score / 5) * 100}%`, background: verdictColor }} />
                  </div>
                  <span>{b.score}/5</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="gf-nav">
        <button className="gf-btn gf-btn-secondary" onClick={onBack}>← Back</button>
        <button className="gf-btn gf-btn-primary" onClick={onNext} disabled={!allScored}>
          Generate Gap Formula →
        </button>
      </div>
    </div>
  );
}
